import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useAppContext } from '../lib/store';
import { Customer } from '../lib/types';
export function CustomerFormPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    customers,
    addCustomer,
    updateCustomer
  } = useAppContext();
  const isEdit = !!id;
  const existingCustomer = isEdit ? customers.find(c => c.id === id) : null;
  const [formData, setFormData] = useState<Partial<Customer>>({
    name: '',
    prefix: '',
    sequence_start: 1,
    address: '',
    postal_code: '',
    city: '',
    tax_number: '',
    contact_person: '',
    email: '',
    phone: '',
    notes: '',
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (existingCustomer) {
      setFormData(existingCustomer);
    }
  }, [existingCustomer]);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name?.trim()) newErrors.name = 'Naziv stranke je obvezen';
    if (!formData.prefix?.trim()) {
      newErrors.prefix = 'Predpona je obvezna';
    } else if (!/^[A-Z]{2,4}$/.test(formData.prefix)) {
      newErrors.prefix = 'Predpona mora vsebovati 2-4 velike črke';
    } else {
      // Check uniqueness
      const duplicate = customers.find(c => c.prefix === formData.prefix && c.id !== id);
      if (duplicate) newErrors.prefix = 'Ta predpona je že v uporabi';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (isEdit && id) {
      updateCustomer(id, formData);
    } else {
      addCustomer(formData as Omit<Customer, 'id' | 'created_at' | 'updated_at'>);
    }
    navigate('/stranke');
  };
  const handleChange = (field: keyof Customer, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-251">
      <PageHeader title={isEdit ? 'Urejanje stranke' : 'Nova stranka'} description="Vnesite podatke o stranki." data-id="element-252" />

      <form onSubmit={handleSubmit} data-id="element-253">
        <Card data-id="element-254">
          <CardContent className="p-6 space-y-8" data-id="element-255">
            {/* General Info */}
            <div className="space-y-4" data-id="element-256">
              <h3 className="text-lg font-medium border-b pb-2" data-id="element-257">
                Osnovni podatki
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-258">
                <div className="space-y-2" data-id="element-259">
                  <Label htmlFor="name" className={errors.name ? 'text-red-500' : ''} data-id="element-260">
                    Naziv stranke *
                  </Label>
                  <Input id="name" value={formData.name} onChange={e => handleChange('name', e.target.value)} className={errors.name ? 'border-red-500' : ''} data-id="element-261" />
                  {errors.name && <p className="text-sm text-red-500" data-id="element-262">{errors.name}</p>}
                </div>

                <div className="space-y-2" data-id="element-263">
                  <Label htmlFor="prefix" className={errors.prefix ? 'text-red-500' : ''} data-id="element-264">
                    Predpona (za številčenje nalogov) *
                  </Label>
                  <Input id="prefix" value={formData.prefix} onChange={e => handleChange('prefix', e.target.value.toUpperCase())} placeholder="Npr. TS" maxLength={4} className={`font-mono uppercase ${errors.prefix ? 'border-red-500' : ''}`} data-id="element-265" />
                  {errors.prefix && <p className="text-sm text-red-500" data-id="element-266">{errors.prefix}</p>}
                  <p className="text-xs text-slate-500" data-id="element-267">
                    2-4 velike črke, npr. TS za Telekom Slovenije
                  </p>
                </div>

                <div className="space-y-2" data-id="element-268">
                  <Label htmlFor="sequence_start" data-id="element-269">
                    Začetna zaporedna številka
                  </Label>
                  <Input id="sequence_start" type="number" min="1" value={formData.sequence_start} onChange={e => handleChange('sequence_start', parseInt(e.target.value) || 1)} data-id="element-270" />
                  <p className="text-xs text-slate-500" data-id="element-271">
                    Številčenje nalogov se začne od te številke (npr. 1 →{' '}
                    {formData.prefix || 'XX'}-26-001)
                  </p>
                </div>

                <div className="space-y-2" data-id="element-272">
                  <Label htmlFor="tax_number" data-id="element-273">Davčna številka</Label>
                  <Input id="tax_number" value={formData.tax_number} onChange={e => handleChange('tax_number', e.target.value)} data-id="element-274" />
                </div>

                <div className="flex items-center space-x-2 pt-8" data-id="element-275">
                  <Switch id="is_active" checked={formData.is_active} onCheckedChange={checked => handleChange('is_active', checked)} data-id="element-276" />
                  <Label htmlFor="is_active" data-id="element-277">Stranka je aktivna</Label>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4" data-id="element-278">
              <h3 className="text-lg font-medium border-b pb-2" data-id="element-279">Naslov</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-280">
                <div className="space-y-2 md:col-span-2" data-id="element-281">
                  <Label htmlFor="address" data-id="element-282">Ulica in hišna številka</Label>
                  <Input id="address" value={formData.address} onChange={e => handleChange('address', e.target.value)} data-id="element-283" />
                </div>
                <div className="space-y-2" data-id="element-284">
                  <Label htmlFor="postal_code" data-id="element-285">Poštna številka</Label>
                  <Input id="postal_code" value={formData.postal_code} onChange={e => handleChange('postal_code', e.target.value)} data-id="element-286" />
                </div>
                <div className="space-y-2" data-id="element-287">
                  <Label htmlFor="city" data-id="element-288">Kraj</Label>
                  <Input id="city" value={formData.city} onChange={e => handleChange('city', e.target.value)} data-id="element-289" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sticky Action Bar */}
        <div className="sticky bottom-0 left-0 right-0 p-4 mt-6 bg-white border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex justify-end gap-3 z-10" data-id="element-290">
          <Button type="button" variant="outline" onClick={() => navigate('/stranke')} data-id="element-291">
            Prekliči
          </Button>
          <Button type="submit" data-id="element-292">Shrani stranko</Button>
        </div>
      </form>
    </div>;
}